import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { api } from './services/api';
import { Layout, LoadingPage } from './components/Layout';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { MyQueuePage } from './pages/MyQueuePage';
import { ComingUpPage } from './pages/ComingUpPage';
import { HistoryPage } from './pages/HistoryPage';
import { WorkOrderDetailPage } from './pages/WorkOrderDetailPage';
import { SchedulePage } from './pages/SchedulePage';
import { ProductionCalendarPage } from './pages/ProductionCalendarPage';
import { TimeOffPage } from './pages/TimeOffPage';
import { ReportProblemPage } from './pages/ReportProblemPage';
import { MyRequestsPage } from './pages/MyRequestsPage';
import { MyReviewsPage } from './pages/MyReviewsPage';
import { TrainingPage } from './pages/TrainingPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { RewardsPage } from './pages/RewardsPage';
import { RecordsPage } from './pages/RecordsPage';
import { ProfilePage } from './pages/ProfilePage';
import { TimeClockPage } from './pages/TimeClockPage';
import { ScanPage } from './pages/ScanPage';
import { DropsPage } from './pages/DropsPage';
import { AddInventoryPage } from './pages/AddInventoryPage';
import { InventoryPage } from './pages/InventoryPage';
import { WorkCardPage } from './pages/WorkCardPage';
import { OrdersPage } from './pages/OrdersPage';
import { PartsPullPage } from './pages/PartsPullPage';
import { SpecialOrdersPage } from './pages/SpecialOrdersPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingPage />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

/**
 * Employees flagged `work_cards_only` never see the work-order detail page
 * (cut sheet / BOM) — they're bounced to the work card for that work order.
 * The page itself is untouched and still works normally for everyone else;
 * this is a per-user redirect, not a removal.
 *
 * Guarding at the route (rather than at each of the six pages that link here)
 * means every entry point is covered — Orders, My Queue, Scan, History,
 * Production Calendar, Special Orders — plus a manually typed URL.
 *
 * Note this is a RESTRICTION, not a grant, so `all_access` deliberately does
 * not bypass it.
 */
function WorkCardOnlyGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const restricted = (user as any)?.permissions?.work_cards_only === true;

  useEffect(() => {
    if (!restricted || !id) return;
    let cancelled = false;
    (async () => {
      try {
        const wo = await api.getWorkOrderDetail(id);
        if (cancelled) return;
        const wsId = (wo as any)?.workstationId;
        if (wsId) {
          navigate(`/work-cards?ws=${wsId}&wo=${id}`, { replace: true });
        } else {
          // No workstation on the work order — nothing to open a card for.
          navigate('/queue', { replace: true });
        }
      } catch {
        if (!cancelled) navigate('/queue', { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [restricted, id, navigate]);

  if (restricted) return <LoadingPage />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingPage />;
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      {/* Reset lands here from the emailed link — works logged-in or out. */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Alias so a token issued for the dealer-portal path still resolves. */}
      <Route path="/forgot-password" element={<Navigate to="/login" replace />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/production-calendar"
        element={
          <ProtectedRoute>
            <ProductionCalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/queue"
        element={
          <ProtectedRoute>
            <MyQueuePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-clock"
        element={
          <ProtectedRoute>
            <TimeClockPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule"
        element={
          <ProtectedRoute>
            <SchedulePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-off"
        element={
          <ProtectedRoute>
            <TimeOffPage />
          </ProtectedRoute>
        }
      />
      <Route path="/report-problem" element={<ProtectedRoute><ReportProblemPage /></ProtectedRoute>} />
      <Route path="/my-requests" element={<ProtectedRoute><MyRequestsPage /></ProtectedRoute>} />
      <Route path="/my-reviews" element={<ProtectedRoute><MyReviewsPage /></ProtectedRoute>} />
      <Route
        path="/training"
        element={
          <ProtectedRoute>
            <TrainingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <DocumentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/records"
        element={
          <ProtectedRoute>
            <RecordsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rewards"
        element={
          <ProtectedRoute>
            <RewardsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coming-up"
        element={
          <ProtectedRoute>
            <ComingUpPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <HistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/work/:id"
        element={
          <ProtectedRoute>
            <WorkCardOnlyGuard>
              <WorkOrderDetailPage />
            </WorkCardOnlyGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scan"
        element={
          <ProtectedRoute>
            <ScanPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/drops"
        element={
          <ProtectedRoute>
            <DropsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <InventoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/add-inventory"
        element={<Navigate to="/inventory" replace />}
      />
      <Route
        path="/work-cards"
        element={
          <ProtectedRoute>
            <WorkCardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <OrdersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/special-orders"
        element={
          <ProtectedRoute>
            <SpecialOrdersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/parts-pull"
        element={
          <ProtectedRoute>
            <PartsPullPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/parts-pull/:orderId"
        element={
          <ProtectedRoute>
            <PartsPullPage />
          </ProtectedRoute>
        }
      />
      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
